import { createClient } from '@supabase/supabase-js';

// Type definitions for our filter counts
export type FilterCountsType = Record<string, Record<string, number>>;

// Sample counts to use as fallback
export const sampleOptionCounts: FilterCountsType = {
  brand: {
    "Alphamega": 14,
    "Brainrocker": 8,
    "Capacitor Partners": 12,
    "Cyfield": 9,
    "Cyta": 11,
  },
  sentiment: {
    "N/A": 5,
    "Negative": 18,
    "Neutral": 27,
    "Positive": 42,
  },
  category: {
    "Charity": 15,
    "Commercial": 32,
    "Sponsorship": 22,
  },
  source: {
    "24h.com.cy": 14,
  },
};

// Create a Supabase client (this is a safe public key)
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Fetches the actual counts from Supabase database
 */
export async function fetchFilterCounts(): Promise<FilterCountsType> {
  try {
    // Initialize the result object
    const result: FilterCountsType = {
      brand: {},
      sentiment: {},
      category: {},
      source: {},
    };

    // Fetch brand counts using raw SQL query
    const { data: brandData, error: brandError } = await supabase
      .rpc('get_brand_counts');

    if (brandError) throw brandError;
    if (brandData) {
      brandData.forEach((item: any) => {
        const brandName = item.brand_name;
        if (brandName) {
          result.brand[brandName] = Number(item.count) || 0;
        }
      });
    }

    // Fetch sentiment counts
    const { data: sentimentData, error: sentimentError } = await supabase
      .rpc('get_sentiment_counts');

    if (sentimentError) throw sentimentError;
    if (sentimentData) {
      sentimentData.forEach((item: any) => {
        const sentiment = item.sentiment;
        if (sentiment) {
          result.sentiment[sentiment] = Number(item.count) || 0;
        }
      });
    }

    // Fetch category counts
    const { data: categoryData, error: categoryError } = await supabase
      .rpc('get_category_counts');

    if (categoryError) throw categoryError;
    if (categoryData) {
      categoryData.forEach((item: any) => {
        const category = item.category;
        if (category) {
          result.category[category] = Number(item.count) || 0;
        }
      });
    }

    // Fetch source counts
    const { data: sourceData, error: sourceError } = await supabase
      .rpc('get_source_counts');

    if (sourceError) throw sourceError;
    if (sourceData) {
      sourceData.forEach((item: any) => {
        const source = item.source;
        if (source) {
          result.source[source] = Number(item.count) || 0;
        }
      });
    }

    return result;
  } catch (error) {
    console.error('Error fetching filter counts:', error);
    return realFilterCounts; // Use our pre-loaded counts instead of sample data
  }
}

// Load real counts into memory - initialized with the results from our SQL queries
export const realFilterCounts: FilterCountsType = {
  brand: {
    "Petrolina": 207,
    "Alphamega": 194,
    "Capacitor Partners": 166,
    "Lidl Cyprus": 140,
    "Cyfield": 107,
    "Esso Cyprus": 97,
    "EKO Cyprus": 96,
    "Cyta": 93,
    "Reflect Festival": 89,
    "Invest Cyprus": 84,
    "TechIsland": 82,
    "Brainrocket": 68,
    "Kotsovolos": 41,
    "Tempo Cyprus": 39,
    "Michael Tyrimos": 32,
    "Quadcode": 15,
    "ecommbx": 7,
    "Michael Gitsis": 5,
    "Demetris Portokaloglou": 4
  },
  sentiment: {
    "Neutral": 566,
    "Positive": 180,
    "Very Positive": 162,
    "N/A": 58,
    "Negative": 12,
    "Very Negative": 3
  },
  category: {
    "Sponsorship": 236,
    "Commercial": 232,
    "Charity": 51
  },
  source: {
    "Kathimerini.com.cy": 301,
    "InBusinessNews": 210,
    "Cyprus Mail": 142,
    "Philenews": 113,
    "Sigmalive": 107,
    "ΠΟΛΙΤΗΣ": 98,
    "Cyprus Times": 65,
    "Reporter Cyprus": 53,
    "fastforward.com.cy": 51,
    "AlphaNews": 44,
    "Politis": 39,
    "Offsite News": 33,
    "Protathlima.com": 29,
    "Limassol Today": 28,
    "Η Σημερινή": 28,
    "elemesos.com": 25,
    "OMADA Cyprus": 24,
    "Brief.com.cy": 24,
    "stockwatch.com.cy": 17,
    "Dialogos": 16,
    "www.stockwatch.com.cy": 12,
    "ΓΗΠΕΔΟ": 11,
    "ShootandGoal": 9,
    "OmegaLive": 9,
    "Goal News": 8,
    "nomisma.com.cy": 7,
    "Eurokerdos": 5,
    "Stockwatch": 5,
    "City Free Press": 3,
    "Unknown Source": 3,
    "Economy Today": 3,
    "To Thema Online": 2,
    "Cosmos News": 1,
    "24h.com.cy": 1,
    "aftodioikisi.com.cy": 1,
    "Famagusta News": 1
  }
};

/**
 * Creates a dynamic option counts object based on your actual data
 * @param items Array of items to count by property
 * @param categoryFields Map of category names to the property names in your items
 * @returns Option counts object for SideFilterSheet
 */
export function generateOptionCounts(
  items: any[],
  categoryFields: Record<string, string>
): Record<string, Record<string, number>> {
  const counts: Record<string, Record<string, number>> = {};

  // Initialize categories
  Object.keys(categoryFields).forEach(category => {
    counts[category] = {};
  });

  // Count occurrences of each value
  items.forEach(item => {
    Object.entries(categoryFields).forEach(([category, field]) => {
      const value = item[field];
      
      // Skip if no value
      if (value === undefined || value === null) return;
      
      // Handle array values
      if (Array.isArray(value)) {
        value.forEach(val => {
          const strVal = String(val);
          counts[category][strVal] = (counts[category][strVal] || 0) + 1;
        });
      } else {
        // Handle single values
        const strVal = String(value);
        counts[category][strVal] = (counts[category][strVal] || 0) + 1;
      }
    });
  });

  return counts;
} 